import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CreditCard, Layers, MessageCircle, Smartphone } from "lucide-react";
import { api, img } from "@/lib/api";
import { ProductGrid } from "@/components/ProductCard";

export const Section = ({ eyebrow, title, to, children, testid, className = "" }) => (
  <section className={`mx-auto mt-20 max-w-7xl px-4 sm:mt-24 sm:px-6 ${className}`} data-testid={testid}>
    <div className="mb-8 flex items-end justify-between gap-4">
      <div>
        {eyebrow && <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#0066FF]">{eyebrow}</p>}
        <h2 className="mt-2 font-display text-3xl font-bold tracking-[-0.03em] text-[#0A0B0E] sm:text-4xl">{title}</h2>
      </div>
      {to && <Link to={to} className="flex shrink-0 items-center gap-1 text-sm font-medium text-slate-500 transition-colors hover:text-[#0066FF]">Vedi tutti <ArrowRight className="h-4 w-4" /></Link>}
    </div>
    {children}
  </section>
);

export const CategoryTiles = ({ cats }) => (
  <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4" data-testid="home-category-tiles">
    {cats.map((c, i) => (
      <Link key={c.slug} to={`/categoria/${c.slug}`} data-testid={`home-category-${c.slug}`}
        className={`group relative flex min-h-44 flex-col justify-end overflow-hidden rounded-3xl bg-slate-100 p-5 ${i === 0 ? "col-span-2 min-h-64 md:row-span-2" : ""}`}>
        {c.image && <img src={img(c.image)} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <span className="relative text-xs font-medium text-white/80">{c.count} prodotti</span>
        <span className={`relative flex items-center justify-between font-display font-bold text-white ${i === 0 ? "text-2xl sm:text-3xl" : "text-lg"}`}>
          <span className="min-w-0">{c.name}</span><ArrowRight className="hidden h-5 w-5 shrink-0 -translate-x-2 opacity-0 transition-[opacity,transform] group-hover:translate-x-0 group-hover:opacity-100 md:block" />
        </span>
      </Link>
    ))}
  </div>
);

export const CategoryShelf = ({ cat, eyebrow, title, testid }) => {
  const [items, setItems] = useState(null);
  const slug = cat?.slug;
  useEffect(() => {
    if (!slug) return;
    api.get("/products", { params: { category: slug, limit: 4 } }).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [slug]);
  if (!cat || (items && !items.length)) return null;
  return (
    <Section eyebrow={eyebrow} title={title} to={`/categoria/${slug}`} testid={testid}>
      <ProductGrid items={items || []} loading={!items} testid={`${testid}-grid`} />
    </Section>
  );
};

export const PromoBanners = ({ banners }) =>
  banners?.length ? (
    <section className="mx-auto mt-20 grid max-w-7xl gap-4 px-4 sm:mt-24 sm:px-6 md:grid-cols-2" data-testid="home-promo-banners">
      {banners.map((b, i) => (
        <Link key={b.id} to={b.link || "/catalogo"} data-testid={`promo-banner-${i}`}
          className="group relative flex aspect-[4/3] items-end overflow-hidden rounded-3xl bg-slate-100 p-7 sm:aspect-[16/10] sm:p-9">
          <img src={img(b.image)} alt={b.title} loading="lazy" style={{ objectPosition: b.position || "center" }}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="relative text-white">
            <h3 className="font-display text-2xl font-bold tracking-[-0.02em] sm:text-3xl">{b.title}</h3>
            {b.subtitle && <p className="mt-2 text-sm text-white/85 sm:text-base">{b.subtitle}</p>}
            {b.button && <span className="mt-5 inline-flex h-11 items-center gap-2 rounded-full bg-white px-5 text-sm font-semibold text-[#0A0B0E] transition-colors group-hover:bg-[#0066FF] group-hover:text-white">{b.button}<ArrowRight className="h-4 w-4" /></span>}
          </div>
        </Link>
      ))}
    </section>
  ) : null;

const ADV = [
  [Layers, "Nuovi, usati premium e ricondizionati", "Un unico catalogo per trovare il dispositivo giusto per te."],
  [Smartphone, "Comodo da smartphone", "Un negozio pensato per essere veloce e semplice ovunque tu sia."],
  [MessageCircle, "Contatto diretto", "Scrivici per un consiglio prima di scegliere."],
  [CreditCard, "Prezzi chiari", "Varianti, extra e sconti sempre visibili prima dell'acquisto."],
];

export const Advantages = () => (
  <Section eyebrow="Perché LeonTech" title="Scegliere è più semplice" testid="home-advantages">
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {ADV.map(([Icon, t, d]) => (
        <div key={t} className="rounded-3xl border border-slate-200 bg-[#F5F7FA] p-6 transition-[transform,border-color] hover:-translate-y-1 hover:border-[#0066FF]/40">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0066FF] text-white"><Icon className="h-5 w-5" /></span>
          <p className="mt-5 font-display font-semibold text-[#0A0B0E]">{t}</p>
          <p className="mt-1.5 text-sm text-slate-500">{d}</p>
        </div>
      ))}
    </div>
  </Section>
);
